/**
 * Audit-ledger widgets (#1805 slice A1).
 *
 * Audit is the only resource in the repo that used all three legacy cell
 * carriers at once, and each needed a different landing spot on `Widget`:
 *
 *   `render`    → `display` + `csvText: stringifyRaw(value)`, the text the
 *                 bespoke-renderer branch of `formatFieldTextForExport`
 *                 already produced.
 *   `transform` → `auditActor`, which reads `record` in BOTH projections. The
 *                 legacy `(value, row) => …` ran ahead of the cell and the
 *                 export alike; a widget that read `value` would print the raw
 *                 `actor_label` and drop the `actor_id` fallback entirely.
 *   `copyable`  → `auditTraceId`, and this one is a trap. `copyable` renders
 *                 through the id widget but exports through the field's own
 *                 TYPE, so re-typing the column `id` would keep the markup
 *                 byte-identical while moving its export from `'' → -` to
 *                 `'' → ''`. The widget therefore borrows the id DISPLAY and
 *                 declares NO `csvText`, leaving the text projection in place.
 *
 * See `features/stocktake/widgets.tsx` for the general argument.
 *
 * ── The three change-history widgets (#1981) ───────────────────────────────
 *
 * `/audit/changes` renders `resource_audit_logs`, whose rows span FOUR resource
 * types. Its three bespoke cells live here rather than beside the bundle for
 * one reason that is a hard constraint, not a preference: this module is walked
 * by `__tests__/architecture/widget-modules-transport-free`, so nothing reached
 * from here may import a `services/` module or `lib/api`. That is why the
 * change summary reads its already-parsed entry and its field index OFF THE
 * ROW (`resourceAuditResource` attaches both in `api.list`) instead of calling
 * `mapResourceAuditLog` or importing the four resource bundles itself — either
 * import would drag the HTTP client into every test suite (#1805 A1's 19 red
 * tests). Type-only imports are erased and therefore fine.
 */
import { Link } from 'react-router-dom';
import type { MouseEvent } from 'react';
import {
  Badge,
  IdCell,
  LIST_TRUNCATE,
  FIELD_TYPE_WIDGETS,
  stringifyRaw,
} from '@resource-kit/ui/react';
import { enumBadgeVariant } from '@resource-kit/ui';
import { registerWidget } from '@resource-kit/controllers';
import type { FieldType, Widget, WidgetCsvContext, WidgetProps } from '@resource-kit/model';
import {
  ChangeSummary,
  ExpandedDetails,
  RESOURCE_DETAIL_PATH,
} from '@components/ui/AuditChangeViews';
import type { FieldIndex } from '@components/ui/AuditChangeViews';
import { lookupEnumLabelOrWarn } from '@lib/enum-label';
import { formatDateTime } from '@lib/datetime';
import { webhookEventPath } from '@lib/routes';
import { failHard } from '@resource-kit/core';
import type { ResourceAuditResourceType, ResourceAuditTimelineItem } from '@services/audit';

declare module '@resource-kit/core' {
  interface WidgetRegistry {
    auditTimestamp: { types: 'datetime'; modes: 'list' };
    auditActor: { types: 'text'; modes: 'list' };
    auditMonoText: { types: 'text'; modes: 'list' };
    auditOutcome: { types: 'enum'; modes: 'list' };
    auditMonoTextWrap: { types: 'text'; modes: 'list' };
    auditTraceId: { types: 'text'; modes: 'list' };
    auditResourceLink: { types: 'id'; modes: 'list' };
    auditChangeSource: { types: 'enum'; modes: 'list' };
    auditChangeSummary: { types: 'text'; modes: 'list' };
  }
}

const EM_DASH = '—';

/**
 * The field type's own widget — level 3 of the resolution order, reached
 * directly because two of the projections below are exactly it.
 */
function typeWidget(type: FieldType): Widget {
  return (
    FIELD_TYPE_WIDGETS.get(type) ??
    failHard(`no default widget is registered for field type "${type}"`)
  );
}

/** The legacy `render` carrier's CSV projection, kept exactly. */
const rawCsv = ({ value }: WidgetCsvContext): string => stringifyRaw(value);

/**
 * Tolerant timestamp formatter (medium date + medium time, no timezone).
 * An unparseable value degrades to the raw string rather than throwing inside
 * a cell renderer — the ledger's whole job is to show what was recorded.
 */
function formatTimestamp(iso: unknown): string {
  const value = typeof iso === 'string' ? iso : '';
  if (!value) return EM_DASH;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return formatDateTime(value, {
    dateStyle: 'medium',
    timeStyle: 'medium',
    showTimezone: false,
  });
}

function AuditTimestamp({ value }: WidgetProps) {
  return <span className="font-mono text-xs">{formatTimestamp(value)}</span>;
}

/**
 * The operator-facing actor: display name, else the raw id, else an em dash.
 *
 * Reads the record because that is what the column means — `actor_label` is
 * nullable and `actor_id` is the fallback. Written against `unknown` rather
 * than the wire type so a row missing both members degrades to the same em
 * dash it always did, instead of throwing in a cell.
 */
function actorLabelOf(record: unknown): string {
  if (typeof record !== 'object' || record === null) return EM_DASH;
  const row: { actor_label?: unknown; actor_id?: unknown } = record;
  const label = typeof row.actor_label === 'string' ? row.actor_label : null;
  const id = typeof row.actor_id === 'string' ? row.actor_id : null;
  return label ?? id ?? EM_DASH;
}

function AuditActor({ record }: WidgetProps) {
  // The markup is the `text` type default's, because that is what the column
  // rendered through once `transform` had resolved the value.
  return <span className="text-sm text-foreground">{actorLabelOf(record) || '-'}</span>;
}

/**
 * The export half of the same projection. `'' → -` is the text type's
 * treatment of an empty string, which is what the transformed value used to
 * reach; every other actor string exports verbatim.
 */
const auditActorCsv = ({ record }: WidgetCsvContext): string => {
  const label = actorLabelOf(record);
  return label === '' ? '-' : label;
};

function AuditMonoText({ value }: WidgetProps) {
  return <span className="font-mono text-xs">{String(value ?? '')}</span>;
}

/** The detail-drawer variant: long paths wrap instead of overflowing the row. */
function AuditMonoTextWrap({ value }: WidgetProps) {
  return <span className="font-mono text-xs break-all">{String(value ?? '')}</span>;
}

/**
 * Fail-loud outcome badge, preserved byte-for-byte from the bespoke page (the
 * `audit-outcome` testid + the `[unknown:*]` drift marker) — the generic enum
 * projection does not fail loud on an out-of-set wire value.
 *
 * BOTH halves read `field.options`, i.e. the MODEL's closed set: `ok` (2xx)
 * carries `color: 'success'` (green) and `error` (4xx/5xx/throw) carries
 * `color: 'destructive'` (red), while an out-of-set wire value matches no
 * option and so lands on `outline` + the `[unknown:*]` marker. Reading the
 * colour off the option rather than holding a second value→variant map here is
 * what keeps this module free of any `@services/*` import — and that is not
 * cosmetic: `src/features/widgets.ts` is loaded by `src/test-setup.ts` for
 * EVERY suite, so a service imported from a widget is instantiated against the
 * real `@lib/api` before any test's `vi.mock` can replace it, and every suite
 * that mocks the HTTP client starts hitting the network instead.
 */
function AuditOutcome({ value, field }: WidgetProps) {
  const outcome = String(value ?? '');
  const options = field.options ?? [];
  const option = options.find((candidate) => String(candidate.value) === outcome);
  return (
    <Badge variant={enumBadgeVariant(option?.color)} data-testid="audit-outcome">
      {lookupEnumLabelOrWarn(outcome, options, 'audit outcome')}
    </Badge>
  );
}

/**
 * Where a ledger row's resource opens (#1981).
 *
 * The table itself lives in `AuditChangeViews` beside `AUDITED_ID_PATH`, which
 * is built out of it: the routes an audit surface can link to are one fact, and
 * the two consumers (this column, and the ids inside a recorded change) must
 * not drift apart. Re-exported here because that is where it was first
 * declared, so existing readers still find it.
 *
 * The ledger stores INTERNAL UUIDs while products / customers are addressed by
 * their Shopify id. Both key spaces reach the same detail route: the backend
 * dispatches on shape (`contexts/commerce/http/id-shape.ts`) and the frontend
 * passes the path param through untouched. That is asserted end to end in
 * `__tests__/resourceAuditResource.idAddressing.test.ts` — read it before
 * changing a target.
 */
export { RESOURCE_DETAIL_PATH };

/** The webhook sources whose rows can name the event that caused them. */
const WEBHOOK_SOURCES = ['shopify_webhook', 'onfleet_webhook'] as const;

function stringMemberOf(record: unknown, key: string): string | null {
  if (typeof record !== 'object' || record === null) return null;
  const value = (record as Record<string, unknown>)[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

function resourceTypeOf(record: unknown): ResourceAuditResourceType | null {
  const raw = stringMemberOf(record, 'resource_type');
  return raw !== null && raw in RESOURCE_DETAIL_PATH ? (raw as ResourceAuditResourceType) : null;
}

/**
 * The resource this row changed, as a SHORT ID that links to its detail page.
 *
 * Deliberately NOT a business name (`#HGS1234`, a product title). The ledger
 * lives in `platform/audit` and stores nothing but the internal UUID; rendering
 * a readable name would mean JOINing `contexts/commerce` tables from platform,
 * which violates G2 (layer direction — platform must not depend on a context).
 * "Just have the backend send a label field" is the same JOIN under another
 * name. The business name is one click away, on the detail page. Changing this
 * needs an ADR first, not a PR.
 */
function AuditResourceLink({ value, record, surface }: WidgetProps) {
  const resourceId = typeof value === 'string' ? value : '';
  const type = resourceTypeOf(record);
  // Both budgets render a UUID the same way (`shortenId` ignores the numeric
  // budget on that branch), so the cell reads identically in the row and in
  // the drawer it opens. They differ only for the OTHER key space this column
  // carries: products / customers are addressed by their Shopify id, and the
  // detail budget leaves that whole (#1978, and the 2026-08-05 UI pass).
  const truncate = surface === 'detail' ? ('uuid' as const) : LIST_TRUNCATE;
  const base = type === null ? null : RESOURCE_DETAIL_PATH[type];
  if (base === null || resourceId === '') {
    return <IdCell value={resourceId} truncate={truncate} />;
  }
  return <IdCell value={resourceId} truncate={truncate} linkTo={`${base}/${resourceId}`} />;
}

/**
 * What caused the change, as a badge — and, for the two webhook sources that
 * recorded an event, a link to that event.
 *
 * `event_id` is the internal `webhook_events` primary key, so `/webhooks/<id>`
 * is a legal route with no lookup (see `AuditTimeline`'s note). A row with a
 * non-webhook source, or a webhook row with no event recorded, renders the bare
 * badge: linking it would fabricate a dead route.
 */
function AuditChangeSource({ value, record, field }: WidgetProps) {
  const source = String(value ?? '');
  const options = field.options ?? [];
  const option = options.find((candidate) => String(candidate.value) === source);
  const badge = (
    <Badge variant={enumBadgeVariant(option?.color)} data-testid="audit-change-source">
      {lookupEnumLabelOrWarn(source, options, 'resource audit source')}
    </Badge>
  );
  const isWebhook = (WEBHOOK_SOURCES as readonly string[]).includes(source);
  const eventId = isWebhook ? stringMemberOf(record, 'event_id') : null;
  if (eventId === null) return badge;
  return (
    <Link
      to={webhookEventPath(eventId)}
      // The row itself is clickable (it opens the drawer); a navigation
      // click must not also open it.
      onClick={(event: MouseEvent<HTMLAnchorElement>) => event.stopPropagation()}
      className="hover:underline underline-offset-2"
    >
      {badge}
    </Link>
  );
}

/**
 * What the row carries for the two change views: the parsed ledger entry and
 * the field index of the resource it belongs to, both attached by
 * `resourceAuditResource`'s `api.list`. See this file's header for why they
 * arrive on the record instead of being computed here.
 */
interface ChangeView {
  entry: ResourceAuditTimelineItem;
  fieldIndex: FieldIndex<never>;
}

function changeViewOf(record: unknown): ChangeView | null {
  if (typeof record !== 'object' || record === null) return null;
  const row = record as { entry?: unknown; fieldIndex?: unknown };
  if (typeof row.entry !== 'object' || row.entry === null) return null;
  if (!(row.fieldIndex instanceof Map)) return null;
  return row as ChangeView;
}

/**
 * The change itself: a one-line summary in a table row, the full diff in the
 * drawer. One widget rather than two because `surface` is exactly this axis —
 * and because both projections are the SAME components the per-record history
 * tab uses (#1979), against a field index chosen by the row's resource type.
 * That is what makes the diff read with the resource's own field labels and
 * value widgets instead of raw wire keys.
 */
function AuditChangeSummary({ record, surface }: WidgetProps) {
  const view = changeViewOf(record);
  if (view === null) return <span className="text-muted-foreground">{EM_DASH}</span>;
  return surface === 'detail' ? (
    <ExpandedDetails entry={view.entry} fieldIndex={view.fieldIndex} />
  ) : (
    <ChangeSummary entry={view.entry} fieldIndex={view.fieldIndex} />
  );
}

/** Called by the feature barrel, which the composition root calls once. */
export function registerAuditWidgets(): void {
  // These rows declare no `input`: resolution refills it from the field type
  // default (ADR-0030).
  registerWidget('auditTimestamp', {
    display: AuditTimestamp,
    csvText: rawCsv,
    supportedTypes: ['datetime'],
    modes: ['list'],
  });
  registerWidget('auditActor', {
    display: AuditActor,
    csvText: auditActorCsv,
    supportedTypes: ['text'],
    modes: ['list'],
  });
  registerWidget('auditMonoText', {
    display: AuditMonoText,
    csvText: rawCsv,
    supportedTypes: ['text'],
    modes: ['list'],
  });
  registerWidget('auditOutcome', {
    display: AuditOutcome,
    csvText: rawCsv,
    supportedTypes: ['enum'],
    modes: ['list'],
  });
  registerWidget('auditMonoTextWrap', {
    display: AuditMonoTextWrap,
    csvText: rawCsv,
    supportedTypes: ['text'],
    modes: ['list'],
  });
  registerWidget('auditTraceId', {
    // The copy affordance, borrowed from the id type's DISPLAY only — see
    // the file header for why `csvText` is deliberately absent here.
    display: typeWidget('id').display,
    supportedTypes: ['text'],
    modes: ['list'],
  });
  registerWidget('auditResourceLink', {
    display: AuditResourceLink,
    // The export is the id itself — the same short-id decision does not
    // apply to a CSV, where the full key is the point.
    csvText: rawCsv,
    supportedTypes: ['id'],
    modes: ['list'],
  });
  registerWidget('auditChangeSource', {
    display: AuditChangeSource,
    csvText: rawCsv,
    supportedTypes: ['enum'],
    modes: ['list'],
  });
  registerWidget('auditChangeSummary', {
    display: AuditChangeSummary,
    // The ledger's own recorded JSON — the only lossless text projection of
    // a diff, and what an operator exporting the ledger actually wants.
    csvText: rawCsv,
    supportedTypes: ['text'],
    modes: ['list'],
  });
}
